import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Define the structure for each row in the dashboard table
export interface SerpRankEntry {
  term: string; // Formatted term (underscores replaced with spaces)
  ppcSales: number | null;
  rank_week_0: number | null;
  rank_week_1: number | null;
  rank_week_2: number | null;
  rank_week_3: number | null;
}

// Define the structure for the data returned by the service
export interface SerpRanksData {
  ranks: SerpRankEntry[];
  generatorUrl: string | null;
}

// Constants for collection paths
const SERP_RANKING_BASE = 'pmt2_serp_ranking';
const CLIENT_URL_BASE = 'pmt2_clienturldatabase';
const URL_ITEM_ID = '88_ SERP Ranking Generator'; // The specific item ID for the URL

/**
 * Fetches SERP ranking data for all terms associated with an ASIN
 * and the relevant generator URL for the company.
 *
 * @param companyName The company name.
 * @param asin The ASIN to fetch data for.
 * @returns An object containing an array of rank entries and the generator URL.
 */
export const getSerpRanksData = async (
  companyName: string,
  asin: string
): Promise<SerpRanksData> => {

  // --- Fetch Generator URL ---
  const fetchUrlPromise = async (): Promise<string | null> => {
    try {
      const urlDocRef = doc(db, CLIENT_URL_BASE, companyName, 'items', URL_ITEM_ID);
      const urlDocSnap = await getDoc(urlDocRef);
      if (urlDocSnap.exists()) {
        const urlData = urlDocSnap.data();
        return urlData?.value || null;
      } else {
        return null;
      }
    } catch (error) {
      return null; // Return null on error
    }
  };

  // --- Fetch SERP Ranks ---
  const fetchRanksPromise = async (): Promise<SerpRankEntry[]> => {
    const ranks: SerpRankEntry[] = [];
    try {
      const termsCollectionRef = collection(
        db,
        SERP_RANKING_BASE,
        companyName,
        'asins',
        asin,
        'terms'
      );
      const q = query(termsCollectionRef); // No specific filtering needed, get all terms
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const termId = docSnap.id; // e.g., "car_wipe"

        // Ensure data exists before processing
        if (data) {
          ranks.push({
            term: termId.replace(/_/g, ' '), // Format term
            ppcSales: typeof data.ppcSales === 'number' ? data.ppcSales : null,
            rank_week_0: typeof data.rank_week_0 === 'number' ? data.rank_week_0 : null,
            rank_week_1: typeof data.rank_week_1 === 'number' ? data.rank_week_1 : null,
            rank_week_2: typeof data.rank_week_2 === 'number' ? data.rank_week_2 : null,
            rank_week_3: typeof data.rank_week_3 === 'number' ? data.rank_week_3 : null,
          });
        } else {
        }
      });
      return ranks;
    } catch (error) {
      return []; // Return empty array on error
    }
  };

  // --- Execute fetches concurrently ---
  try {
    const [generatorUrl, ranks] = await Promise.all([
      fetchUrlPromise(),
      fetchRanksPromise(),
    ]);

    return { ranks, generatorUrl };
  } catch (error) {
    // This catch block might be redundant if individual promises handle errors,
    // but included for safety.
    return { ranks: [], generatorUrl: null }; // Return default structure on overall error
  }
};